import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router";
import { Box, CircularProgress, Container, Stack, TextField, Typography } from "@mui/material";

import learnApi from "../../api/learnApi";

const METRIC_COLUMNS = [
  {
    title: "Financial & Valuation",
    groups: [
      {
        title: "Financial Metrics",
        slugs: [
          "cash-and-treasury",
          "cash-runway",
          "current-ratio",
          "ebitda",
          "free-cash-flow",
          "interest-coverage",
          "net-debt",
          "operating-margin-net-margin",
          "roa-roe",
        ],
      },
      {
        title: "Valuation",
        slugs: ["market-cap-vs-enterprise-value", "ev-ebitda", "fcf-yield"],
      },
    ],
  },
  {
    title: "Assets & Geology",
    groups: [
      {
        title: "Exploration & Geology",
        slugs: ["drill-results", "district-position", "land-position"],
      },
      {
        title: "Resources & Reserves",
        slugs: [
          "resources-vs-reserves",
          "measured-indicated-inferred",
          "proven-probable-reserves",
          "resource-classification-mix",
          "in-situ-value",
          "ev-per-resource-ounce",
          "lifecycle-adjusted-resource-valuation",
        ],
      },
    ],
  },
  {
    title: "Risk & Company",
    groups: [
      {
        title: "Financing",
        slugs: ["financing-dependence", "share-dilution", "treasury-change"],
      },
      {
        title: "Risk & Jurisdiction",
        slugs: ["jurisdiction-risk", "political-stability-vs-mining-policy", "permitting-risk", "project-legal-risk"],
      },
      {
        title: "Lifecycle & Structure",
        slugs: ["mining-lifecycle", "lassonde-curve", "trading-liquidity", "asset-concentration"],
      },
    ],
  },
];

const FRAMEWORK_COLUMNS = [
  {
    title: "Evaluate a Company",
    slugs: [
      "evaluate-exploration-company",
      "evaluate-development-company",
      "evaluate-producing-company",
      "what-makes-a-mining-company-investable",
    ],
  },
  {
    title: "Evaluate the Evidence",
    slugs: [
      "evaluate-drill-results",
      "evaluate-mineral-resource",
      "evaluate-mining-catalysts",
      "evaluate-jurisdiction-risk",
      "evaluate-multi-asset-company",
    ],
  },
  {
    title: "Build the Investment Case",
    slugs: [
      "build-mining-investment-thesis",
      "evaluate-mining-valuation",
      "evaluate-financing-dilution-risk",
      "evaluate-mining-balance-sheet",
      "cheap-for-a-reason",
      "risk-vs-opportunity",
      "compare-mining-lifecycle-stages",
    ],
  },
];

const LEARNING_PATHS = [
  {
    title: "Mining Fundamentals",
    description: "Start with the lifecycle, mineral inventory, resource confidence and early technical evidence that shape mining analysis.",
    startSlugs: ["mining-lifecycle", "resources-vs-reserves", "measured-indicated-inferred", "proven-probable-reserves"],
    href: "#metrics-concepts",
  },
  {
    title: "Financial Strength & Cash Flow",
    description: "Understand whether a mining company can fund its assets, withstand financial pressure and generate cash for shareholders.",
    startSlugs: ["cash-runway", "free-cash-flow", "net-debt", "evaluate-mining-balance-sheet"],
    href: "#investing-frameworks",
  },
  {
    title: "Exploration & Asset Quality",
    description: "Evaluate drilling, district context, land position and resource quality before treating exploration upside as investable.",
    startSlugs: ["evaluate-drill-results", "evaluate-mineral-resource", "district-position", "land-position"],
    href: "#metrics-concepts",
  },
  {
    title: "Risk & Jurisdiction",
    description: "Assess whether location, permitting, political conditions and project-level legal risk can change the investment case.",
    startSlugs: ["jurisdiction-risk", "political-stability-vs-mining-policy", "permitting-risk", "project-legal-risk"],
    href: "#metrics-concepts",
  },
  {
    title: "Valuation & Capital Structure",
    description: "Connect enterprise value, resource valuation, dilution and financing dependence to the stage of the company.",
    startSlugs: ["market-cap-vs-enterprise-value", "ev-per-resource-ounce", "share-dilution", "financing-dependence"],
    href: "#metrics-concepts",
  },
  {
    title: "Building the Investment Case",
    description: "Turn individual metrics and evidence into a complete view of quality, risk, valuation and catalysts.",
    startSlugs: [
      "build-mining-investment-thesis",
      "what-makes-a-mining-company-investable",
      "compare-mining-lifecycle-stages",
      "risk-vs-opportunity",
    ],
    href: "#investing-frameworks",
  },
];

const PRODUCT_GUIDES = [
  {
    eyebrow: "Guide",
    title: "Mining Company Directory",
    description: "Discover companies and move into deeper research.",
    slug: "resource-nav-directory",
  },
  {
    eyebrow: "Guide",
    title: "Company Profile",
    description: "Understand an individual company across financials, assets, resources, valuation and risk.",
    slug: "resource-nav-company-profile",
  },
  {
    eyebrow: "Guide",
    title: "Curated Rankings",
    description: "Explore companies through predefined analytical comparisons.",
    slug: "resource-nav-curated-rankings",
  },
  {
    eyebrow: "Guide",
    title: "Mining Stock Screener",
    description: "Build custom peer groups, filters and comparisons.",
    slug: "resource-nav-screener",
  },
];

const METHODOLOGY_GUIDES = [
  {
    eyebrow: "Methodology",
    title: "Data Methodology",
    description: "Sources, standardization, resource and reserve treatment, calculated metrics, missing data and limitations.",
    slug: "resource-nav-data-methodology",
  },
  {
    eyebrow: "Methodology",
    title: "Scoring Methodology",
    description: "What ResourceNAV scores measure, the evidence they consider, how to interpret them and their limitations.",
    slug: "resource-nav-scoring-methodology",
  },
];

const normalizeArticles = (data) => (Array.isArray(data) ? data : data?.results || []);

function compareArticle(left, right) {
  return String(left.title || "").localeCompare(String(right.title || ""));
}

function articleMap(articles) {
  return new Map(articles.map((article) => [article.slug, article]));
}

function articlesForSlugs(bySlug, slugs) {
  return slugs.map((slug) => bySlug.get(slug)).filter(Boolean);
}

function remainingArticles(articles, usedSlugs, articleType) {
  return articles
    .filter((article) => article.article_type === articleType && !usedSlugs.has(article.slug))
    .slice()
    .sort(compareArticle);
}

function SectionHeader({ title, description }) {
  return (
    <Box sx={{ mb: { xs: 2.25, md: 3 } }}>
      <Typography component="h2" sx={{ fontSize: { xs: 25, md: 31 }, fontWeight: 900, lineHeight: 1.1 }}>
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.9, maxWidth: 780, lineHeight: 1.6 }}>
        {description}
      </Typography>
    </Box>
  );
}

function ResponsiveGrid({ children, columns = 3, rowGap = 2 }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: `repeat(${columns}, minmax(0, 1fr))` },
        columnGap: { md: 3, lg: 4 },
        rowGap,
      }}
    >
      {children}
    </Box>
  );
}

function DiscoveryArticleLink({ article }) {
  return (
    <Box
      component={RouterLink}
      to={`/learn/${article.slug}`}
      sx={{
        display: "block",
        py: 0.35,
        color: "text.primary",
        textDecoration: "none",
        "&:hover": { color: "primary.main" },
        "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 750 }}>
        {article.title}
      </Typography>
    </Box>
  );
}

function LearningPathCard({ path, bySlug, accented = false }) {
  const articles = articlesForSlugs(bySlug, path.startSlugs);

  return (
    <Box
      sx={(theme) => ({
        ...theme.mixins.frostedBox,
        p: { xs: 2.5, md: 3 },
        minHeight: 255,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: 1,
        position: "relative",
        overflow: "hidden",
        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        "&:before": accented
          ? {
          content: '""',
          position: "absolute",
          left: 24,
          right: 24,
          top: 0,
          height: 3,
          backgroundColor: "primary.main",
          borderRadius: "0 0 3px 3px",
          }
          : undefined,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 2,
          borderColor: "rgba(15, 23, 42, 0.2)",
        },
      })}
    >
      <Typography component="h3" sx={{ fontSize: 19, fontWeight: 850, lineHeight: 1.25 }}>
        {path.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.65 }}>
        {path.description}
      </Typography>
      {articles.length ? (
        <Stack spacing={0.85} sx={{ mt: 2.25 }}>
          {articles.map((article) => (
            <DiscoveryArticleLink key={article.slug} article={article} />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

function StartSection({ bySlug }) {
  return (
    <Box component="section" sx={{ mb: { xs: 5.5, md: 7 } }}>
      <SectionHeader
        title="Start With a Learning Path"
        description="Choose a topic based on what you're trying to understand or evaluate."
      />
      <ResponsiveGrid columns={2} rowGap={2}>
        {LEARNING_PATHS.map((path, index) => (
          <LearningPathCard key={path.title} path={path} bySlug={bySlug} accented={index === 0} />
        ))}
      </ResponsiveGrid>
    </Box>
  );
}

function ProductNavCard({ item, bySlug }) {
  const article = bySlug.get(item.slug);
  const linkProps = article
    ? {
        component: RouterLink,
        to: `/learn/${article.slug}`,
      }
    : {};

  return (
    <Box
      {...linkProps}
      aria-disabled={article ? undefined : true}
      sx={(theme) => ({
        ...theme.mixins.frostedBox,
        display: "block",
        p: { xs: 1.9, md: 2 },
        minHeight: { xs: 0, md: 116 },
        color: "text.primary",
        textDecoration: "none",
        backgroundColor: "#fff",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: 0,
        opacity: article ? 1 : 0.66,
        transition: article ? "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease" : undefined,
        "&:hover": article
          ? {
              transform: "translateY(-2px)",
              boxShadow: 2,
              borderColor: "rgba(15, 23, 42, 0.2)",
            }
          : undefined,
        "&:focus-visible": article
          ? {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 3,
            }
          : undefined,
      })}
    >
      <Typography
        component="div"
        sx={{
          mb: 0.85,
          fontSize: 11,
          fontWeight: 850,
          letterSpacing: 0.8,
          lineHeight: 1,
          textTransform: "uppercase",
          color: "primary.main",
        }}
      >
        {item.eyebrow}
      </Typography>
      <Typography component="h3" sx={{ fontSize: { xs: 17, md: 18 }, fontWeight: 900, lineHeight: 1.22 }}>
        {item.title}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.85, fontSize: 14.5, lineHeight: 1.55 }}>
        {item.description}
      </Typography>
    </Box>
  );
}

function ProductNavigationSection({ bySlug }) {
  return (
    <Box component="section" sx={{ mb: { xs: 5.5, md: 7 } }}>
      <SectionHeader
        title="Using ResourceNAV"
        description="Learn how to discover, compare and evaluate mining companies using ResourceNAV."
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        {PRODUCT_GUIDES.map((item) => (
          <ProductNavCard key={item.title} item={item} bySlug={bySlug} />
        ))}
      </Box>

      <Box sx={{ mt: { xs: 4.5, md: 5.5 } }}>
        <Typography component="h2" sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 900, lineHeight: 1.12 }}>
          Methodology
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.8, maxWidth: 780, lineHeight: 1.6 }}>
          Understand how ResourceNAV standardizes data and how its scoring framework should be interpreted.
        </Typography>
        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {METHODOLOGY_GUIDES.map((item) => (
            <ProductNavCard key={item.title} item={item} bySlug={bySlug} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CompactArticleLink({ article }) {
  return (
    <Box
      component={RouterLink}
      to={`/learn/${article.slug}`}
      sx={{
        display: "block",
        py: 0.45,
        color: "text.primary",
        textDecoration: "none",
        fontSize: 14,
        lineHeight: 1.35,
        fontWeight: 560,
        "&:hover": { color: "primary.main" },
        "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
      }}
    >
      {article.title}
    </Box>
  );
}

function CompactArticleGroup({ title, articles }) {
  if (!articles.length) return null;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="h5"
        sx={{ fontSize: 13.5, fontWeight: 760, lineHeight: 1.25, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}
      >
        {title}
      </Typography>
      <Stack spacing={0} sx={{ mt: 0.75 }}>
        {articles.map((article) => (
          <CompactArticleLink key={article.slug} article={article} />
        ))}
      </Stack>
    </Box>
  );
}

function LibraryColumn({ title, children }) {
  return (
    <Stack
      spacing={{ xs: 2.1, md: 2.5 }}
      sx={{
        minWidth: 0,
        alignSelf: "start",
      }}
    >
      {title ? (
        <Typography component="h4" sx={{ fontSize: 18, fontWeight: 900, lineHeight: 1.18 }}>
          {title}
        </Typography>
      ) : null}
      {children}
    </Stack>
  );
}

function LibrarySection({ id, title, description, count, children }) {
  return (
    <Box
      id={id}
      component="section"
      sx={{
        scrollMarginTop: 88,
        mb: { xs: 4.75, md: 6 },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" },
          gap: 1.5,
          alignItems: "end",
          mb: { xs: 2.25, md: 2.75 },
        }}
      >
        <Box>
          <Typography component="h3" sx={{ fontSize: { xs: 23, md: 27 }, fontWeight: 900, lineHeight: 1.12 }}>
            {title}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 740, lineHeight: 1.55 }}>
            {description}
          </Typography>
        </Box>
        <Typography color="text.secondary" sx={{ whiteSpace: "nowrap", fontWeight: 850 }}>
          {count} article{count === 1 ? "" : "s"}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

function articleMatches(article, search) {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return [article.title, article.description, article.category, article.article_type]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(query));
}

function filterArticles(articles, search) {
  return articles.filter((article) => articleMatches(article, search));
}

function MetricsLibrary({ articles, bySlug }) {
  const configuredSlugs = new Set(METRIC_COLUMNS.flatMap((column) => column.groups.flatMap((group) => group.slugs)));
  const ungrouped = remainingArticles(articles, configuredSlugs, "metric_concept");
  const metricCount = articles.filter((article) => article.article_type === "metric_concept").length;
  if (!metricCount) return null;

  return (
    <LibrarySection
      id="metrics-concepts"
      title="Metrics & Concepts"
      description="Browse financial, valuation, asset, risk and lifecycle concepts."
      count={metricCount}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
          columnGap: { md: 4, lg: 5 },
          rowGap: 3,
          alignItems: "start",
        }}
      >
        {METRIC_COLUMNS.map((column, index) => (
          <LibraryColumn key={column.title} title={column.title}>
            {column.groups.map((group) => (
              <CompactArticleGroup key={group.title} title={group.title} articles={articlesForSlugs(bySlug, group.slugs)} />
            ))}
            {index === METRIC_COLUMNS.length - 1 ? <CompactArticleGroup title="More Concepts" articles={ungrouped} /> : null}
          </LibraryColumn>
        ))}
      </Box>
    </LibrarySection>
  );
}

function FrameworksLibrary({ articles, bySlug }) {
  const configuredSlugs = new Set(FRAMEWORK_COLUMNS.flatMap((column) => column.slugs));
  const ungrouped = remainingArticles(articles, configuredSlugs, "investing_framework");
  const frameworkCount = articles.filter((article) => article.article_type === "investing_framework").length;
  if (!frameworkCount) return null;

  return (
    <LibrarySection
      id="investing-frameworks"
      title="Investing Frameworks"
      description="Browse structured frameworks for evaluating companies, evidence, valuation and risk."
      count={frameworkCount}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
          columnGap: { md: 4, lg: 5 },
          rowGap: 3,
          alignItems: "start",
        }}
      >
        {FRAMEWORK_COLUMNS.map((column) => (
          <LibraryColumn key={column.title} title={column.title}>
            <Stack spacing={0}>
              {articlesForSlugs(bySlug, column.slugs).map((article) => (
                <CompactArticleLink key={article.slug} article={article} />
              ))}
            </Stack>
          </LibraryColumn>
        ))}
      </Box>
      {ungrouped.length ? (
        <Box sx={{ mt: 3, maxWidth: { md: "calc((100% - 64px) / 3)" } }}>
          <CompactArticleGroup title="More Frameworks" articles={ungrouped} />
        </Box>
      ) : null}
    </LibrarySection>
  );
}

function ProductHelpLibrary({ articles }) {
  const productArticles = articles.filter((article) => article.article_type === "product_help").slice().sort(compareArticle);
  if (!productArticles.length) return null;
  const productBySlug = articleMap(productArticles);
  const guideSlugs = PRODUCT_GUIDES.map((item) => item.slug);
  const methodologySlugs = METHODOLOGY_GUIDES.map((item) => item.slug);
  const configuredSlugs = new Set([...guideSlugs, ...methodologySlugs]);
  const guideArticles = articlesForSlugs(productBySlug, guideSlugs);
  const methodologyArticles = articlesForSlugs(productBySlug, methodologySlugs);
  const otherArticles = productArticles.filter((article) => !configuredSlugs.has(article.slug));

  return (
    <LibrarySection
      id="using-resourcenav"
      title="Product Help"
      description="Browse product-focused guides for applying ResourceNAV analysis workflows."
      count={productArticles.length}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
          columnGap: { md: 4, lg: 5 },
          rowGap: 3,
        }}
      >
        {guideArticles.length ? (
          <LibraryColumn title="Using ResourceNAV">
            <CompactArticleGroup title="Product Help" articles={guideArticles} />
          </LibraryColumn>
        ) : null}
        {methodologyArticles.length ? (
          <LibraryColumn title="Methodology">
            <CompactArticleGroup title="Methodology" articles={methodologyArticles} />
          </LibraryColumn>
        ) : null}
        {otherArticles.length ? (
          <LibraryColumn title="More Product Help">
            <CompactArticleGroup title="Product Help" articles={otherArticles} />
          </LibraryColumn>
        ) : null}
      </Box>
    </LibrarySection>
  );
}

function CompleteLibrarySection({ articles, search, setSearch }) {
  const visibleArticles = filterArticles(articles, search);
  const visibleBySlug = articleMap(visibleArticles);
  const hasMetrics = visibleArticles.some((article) => article.article_type === "metric_concept");
  const hasFrameworks = visibleArticles.some((article) => article.article_type === "investing_framework");

  return (
    <Box component="section" sx={{ pt: { xs: 3.5, md: 4 }, borderTop: "1px solid rgba(34, 35, 37, 0.18)" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
          gap: 2,
          alignItems: "end",
          mb: 2.25,
        }}
      >
        <Box>
          <Typography component="h2" sx={{ fontSize: { xs: 25, md: 31 }, fontWeight: 900, lineHeight: 1.1 }}>
            Complete Learn Library
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.9, maxWidth: 780, lineHeight: 1.6 }}>
            Browse every ResourceNAV Learn article by topic.
          </Typography>
        </Box>
        <Typography color="text.secondary" sx={{ fontWeight: 850, whiteSpace: "nowrap" }}>
          {visibleArticles.length} article{visibleArticles.length === 1 ? "" : "s"}
        </Typography>
      </Box>

      <Box
        sx={{
          mb: { xs: 3, md: 4 },
        }}
      >
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search all articles..."
          fullWidth
          sx={{
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              "&.Mui-focused fieldset": { borderColor: "primary.main" },
            },
          }}
        />
      </Box>

      <Box
        sx={(theme) => ({
          ...theme.mixins.frostedBox,
          pt: { xs: 3.5, md: 4 },
          px: { xs: 2.25, md: 4 },
          pb: { xs: 0.5, md: 1 },
          background: "rgba(255, 255, 255, 0.68)",
        })}
      >
        {visibleArticles.length ? (
          <>
            <MetricsLibrary articles={visibleArticles} bySlug={visibleBySlug} />
            {hasMetrics && hasFrameworks ? <Box sx={{ my: { xs: 3.5, md: 4.5 }, borderTop: "1px solid rgba(34, 35, 37, 0.12)" }} /> : null}
            <FrameworksLibrary articles={visibleArticles} bySlug={visibleBySlug} />
            <ProductHelpLibrary articles={visibleArticles} />
          </>
        ) : (
          <Box sx={{ py: 4 }}>
            <Typography sx={{ fontWeight: 850 }}>No Learn articles match that search.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function LearnPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let ignore = false;
    learnApi
      .getLearnArticles()
      .then((data) => {
        if (!ignore) setArticles(normalizeArticles(data));
      })
      .catch(() => {
        if (!ignore) setError("Learn articles are unavailable right now.");
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, []);

  const bySlug = useMemo(() => articleMap(articles), [articles]);

  return (
    <Container maxWidth={false} sx={{ maxWidth: 1320, mt: { xs: 4, md: 5 }, mb: 8 }}>
      <Box sx={{ mb: { xs: 4, md: 5 } }}>
        <Typography variant="h3" sx={{ fontSize: { xs: 32, md: 40 }, fontWeight: 900, lineHeight: 1.08 }}>
          Learn
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1.1, maxWidth: 780, lineHeight: 1.65 }}>
          Understand the metrics, concepts, and frameworks used to evaluate mining companies and use ResourceNAV effectively.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : articles.length ? (
        <>
          <StartSection bySlug={bySlug} />
          <ProductNavigationSection bySlug={bySlug} />
          <CompleteLibrarySection
            articles={articles}
            search={search}
            setSearch={setSearch}
          />
        </>
      ) : (
        <Box sx={{ py: 5, borderTop: "1px solid rgba(34, 35, 37, 0.16)" }}>
          <Typography sx={{ fontWeight: 850, fontSize: 18 }}>No Learn articles are published yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            Published articles will appear here as the ResourceNAV reference library grows.
          </Typography>
        </Box>
      )}
    </Container>
  );
}
